import { Theme, useMediaQuery } from '@mui/material';
import { DatePicker, DatePickerProps, DateView } from '@mui/x-date-pickers';

import { DatePickerSlots } from '@mui/x-date-pickers/DatePicker';

import addYears from 'date-fns/addYears';
import { RefAttributes, useMemo } from 'react';
import DatePickerAutocompleteField from './DatePickerAutocompleteField';

type OptionalKeys<T extends object> = Exclude<
  {
    [K in keyof T]: NonNullable<unknown> extends Pick<T, K> ? K : never;
  }[keyof T],
  undefined
>;
type UncapitalizeKeys<T extends object> = Uncapitalize<keyof T & string>;
export type UncapitalizeObjectKeys<T extends object> = {
  [key in UncapitalizeKeys<Pick<T, OptionalKeys<T>>>]?: Capitalize<key> extends keyof T
    ? T[Capitalize<key>]
    : never;
} & {
  [key in UncapitalizeKeys<Omit<T, OptionalKeys<T>>>]: Capitalize<key> extends keyof T
    ? T[Capitalize<key>]
    : never;
};

const MIN_DATE_DEFAULT = addYears(new Date(), -3);

export type DatePickerFlexSalesView = DateView;
export interface DatePickerFlexSalesProps<TDate>
  extends Omit<DatePickerProps<any>, 'views'>,
    RefAttributes<HTMLDivElement> {
  spacing?: number;
  views?: DatePickerFlexSalesView[];
  required?: boolean;
  error?: string;
  name?: string;
}

const slots: DatePickerProps<Date>['slots'] = {
  field: DatePickerAutocompleteField,
};

const DatePickerFlexSales = <TDate,>({
  minDate: minDateParam,
  maxDate: maxDateparam,
  spacing,
  required,
  inputRef,
  error,
  views,
  name,
  ...others
}: DatePickerFlexSalesProps<TDate>) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const minDate = useMemo(() => minDateParam || MIN_DATE_DEFAULT, [minDateParam]) as TDate;
  const maxDate = useMemo(() => maxDateparam || new Date(), [maxDateparam]) as TDate;

  const slotProps: DatePickerProps<Date>['slotProps'] = useMemo(
    () => ({
      field: {
        views,
        minDate,
        maxDate,
        required,
        fullWidth: true,
        helperText: error,
        error: Boolean(error),
        name,
      } as never,
    }),
    [required, error, minDate, maxDate, views, name]
  );

  return (
    <DatePicker
      minDate={minDate as Date}
      maxDate={maxDate as Date}
      slotProps={slotProps}
      selectedSections="day"
      slots={!isMobile ? (slots as UncapitalizeObjectKeys<DatePickerSlots<Date>>) : undefined}
      views={views}
      {...others}
    />
  );
};

export default DatePickerFlexSales;
